<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title>
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Add Bank Detail
        </v-card-title>
        <v-card-text>
          <!-- Bank Verification Section -->
          <v-card v-if="!isVerified" class="mb-6" elevation="2" rounded="lg">
            <v-card-title class="primary white--text">
              <v-icon left color="white">mdi-shield-check</v-icon>
              Bank Detail Verification
            </v-card-title>
            <v-card-text class="pt-4">
              <v-row>
                <v-col sm="6">
                  <v-autocomplete
                    ref="user"
                    outlined
                    v-model="verificationForm.user_id"
                    :items="userItems"
                    :loading="isLoadingUsers"
                    :search-input.sync="userSearch"
                    label="Search and Select User *"
                    prepend-inner-icon="mdi-account-outline"
                    :error-messages="verificationErrors.user_id"
                    no-data-text="Type to search users"
                    item-text="text"
                    item-value="value"
                    clearable
                    required
                  ></v-autocomplete>
                </v-col>
                <v-col sm="6">
                  <v-text-field
                    v-model="verificationForm.account_number"
                    outlined
                    label="Account Number *"
                    prepend-inner-icon="mdi-credit-card"
                    :error-messages="verificationErrors.account_number"
                    required
                  ></v-text-field>
                </v-col>
                <v-col sm="6">
                  <v-text-field
                    v-model="verificationForm.ifsc"
                    outlined
                    label="IFSC Code *"
                    prepend-inner-icon="mdi-code-string"
                    :error-messages="verificationErrors.ifsc"
                    placeholder="e.g., SBIN0001234"
                    required
                  ></v-text-field>
                </v-col>
                <v-col sm="6" class="d-flex align-center">
                  <v-btn
                    :loading="isVerifying"
                    :disabled="isVerifying || !canVerify"
                    color="success"
                    dark
                    @click="verifyBankDetails"
                    large
                  >
                    <v-icon left>mdi-shield-check</v-icon>
                    Verify Bank Details
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Verified Bank Details Display -->
          <v-card
            v-if="isVerified && verifiedData"
            class="mb-6"
            elevation="2"
            rounded="lg"
          >
            <v-card-title class="success white--text">
              <v-icon left color="white">mdi-check-circle</v-icon>
              Verified Bank Details
            </v-card-title>
            <v-card-text class="pt-4">
              <v-row>
                <!-- Primary Bank Information -->
                <v-col cols="12">
                  <h4 class="mb-3 text-success">✅ Verified Bank Information</h4>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :value="verifiedData.name"
                    outlined
                    label="Account Holder Name"
                    prepend-inner-icon="mdi-account"
                    readonly
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :value="verifiedData.account_number"
                    outlined
                    label="Account Number"
                    prepend-inner-icon="mdi-credit-card"
                    readonly
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :value="verifiedData.bank_name"
                    outlined
                    label="Bank Name"
                    prepend-inner-icon="mdi-bank"
                    readonly
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :value="verifiedData.branch"
                    outlined
                    label="Branch Name"
                    prepend-inner-icon="mdi-office-building"
                    readonly
                  ></v-text-field>
                </v-col>
                
                <!-- Bank Codes & Location -->
                <v-col cols="12">
                  <h4 class="mb-3 mt-4 text-success">Bank Codes & Location</h4>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    :value="verifiedData.ifsc"
                    outlined
                    label="IFSC Code"
                    prepend-inner-icon="mdi-code-string"
                    readonly
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    :value="verifiedData.micr"
                    outlined
                    label="MICR Code"
                    prepend-inner-icon="mdi-numeric"
                    readonly
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    :value="verifiedData.city"
                    outlined
                    label="City"
                    prepend-inner-icon="mdi-map-marker"
                    readonly
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" class="text-center mt-4">
                  <v-btn
                    color="primary"
                    large
                    @click="resetVerification"
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    Verify Another Account
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-container>
    <v-dialog v-model="dialogVisible" width="600" height="1000">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon
                large
                :style="
                  dialogSuccess
                    ? 'color: green; font-size: 100px; width: 200px !important'
                    : 'color: red; font-size: 100px; width: 200px !important'
                "
              >
                {{
                  dialogSuccess
                    ? "mdi-check-circle-outline"
                    : "mdi-alert-remove-outline"
                }}
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <v-row>
                  <v-col>
                    <h1 :style="dialogSuccess ? 'color: green' : 'color: red'">
                      {{ dialogSuccess ? "SUCCESS!" : "OOPS!" }}
                    </h1>
                    <br />
                    <h3>{{ dialogMessage }}</h3>
                    <br />
                    <v-btn
                      :color="dialogSuccess ? 'green' : 'red'"
                      @click="handleDialogClose"
                    >
                      OK
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </v-main>
</template>
<script>
import axios from "axios";
export default {
  name: "BankDetailCreatePage",
  data() {
    return {
      verificationForm: {
        user_id: null,
        account_number: "",
        ifsc: "",
      },
      verificationErrors: {},
      isVerifying: false,
      isVerified: false,
      verifiedData: null,
      dialogVisible: false,
      dialogMessage: "",
      dialogSuccess: false,
      accountTypeItems: [
        { id: 1, text: "Savings Account", value: 1 },
        { id: 2, text: "Current Account", value: 2 },
        { id: 3, text: "Fixed Deposit", value: 3 },
        { id: 4, text: "Recurring Deposit", value: 4 },
        { id: 5, text: "NRI Account", value: 5 },
      ],
      userItems: [],
      isLoadingUsers: false,
      userSearch: null,
      searchTimeout: null,
    };
  },
  computed: {
    canVerify() {
      return (
        this.verificationForm.user_id &&
        this.verificationForm.account_number &&
        this.verificationForm.ifsc
      );
    },
  },
  watch: {
    userSearch(val) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(() => {
        this.searchUsers(val);
      }, 300);
    },
  },
  mounted() {
    // Don't load all users on mount
  },
  methods: {
    async getMasters() {
      await axios
        .get(`bank_details/masters`)
        .then((masters) => {
          masters = masters.data;
          // User
          masters.users.forEach((user) => {
            this.userItems.push({
              id: user.id,
              text: user.name,
              value: user.id,
            });
          });
        })
        .catch(function (error) {
          console.log(error);
          // redirect to Home Page
          window.location.href = "/auth/login";
        });
    },
    async searchUsers(searchTerm) {
      if (!searchTerm || searchTerm.length < 2) {
        this.userItems = [];
        return;
      }

      try {
        this.isLoadingUsers = true;
        const response = await axios.get(`users?search=${searchTerm}&limit=50`);
        
        if (response.data.success && response.data.data) {
          this.userItems = response.data.data.map((user) => ({
            id: user.id,
            text: `${user.first_name}${user.last_name ? ' ' + user.last_name : ''} (${user.phone})`,
            value: user.id,
          }));
        }
      } catch (error) {
        console.log("Error searching users:", error);
        this.userItems = [];
      } finally {
        this.isLoadingUsers = false;
      }
    },
    
    async getUsers() {
      // Legacy method - now only loads initial data if needed
      try {
        const response = await axios.get("users?limit=20");
        if (response.data.success && response.data.data) {
          this.userItems = response.data.data.map((user) => ({
            id: user.id,
            text: `${user.first_name}${user.last_name ? ' ' + user.last_name : ''} (${user.phone})`,
            value: user.id,
          }));
        }
      } catch (error) {
        console.log("Error loading users:", error);
      }
    },
    async verifyBankDetails() {
      try {
        this.isVerifying = true;
        this.verificationErrors = {};

        const response = await axios.post(
          "/bank_details/verify",
          this.verificationForm
        );

        if (response.data.success) {
          this.verifiedData = response.data.data.bank_detail;
          this.isVerified = true;
          this.dialogMessage = "Bank details verified and saved successfully!";
          this.dialogSuccess = true;
          this.dialogVisible = true;
        } else {
          this.dialogMessage =
            response.data.message || "Bank verification failed";
          this.dialogSuccess = false;
          this.dialogVisible = true;
        }
      } catch (e) {
        console.error("Error verifying bank details:", e);

        if (e.response && e.response.status === 422) {
          this.verificationErrors = e.response.data.errors || {};
          const firstError = Object.values(this.verificationErrors)[0];
          this.dialogMessage = firstError
            ? firstError[0]
            : "Please check your input data";
        } else {
          this.dialogMessage =
            e.response?.data?.message ||
            "An error occurred during bank verification";
        }

        this.dialogSuccess = false;
        this.dialogVisible = true;
      } finally {
        this.isVerifying = false;
      }
    },
    resetVerification() {
      this.isVerified = false;
      this.verifiedData = null;
      this.verificationForm = {
        user_id: null,
        account_number: "",
        ifsc: "",
      };
      this.verificationErrors = {};
    },
    handleDialogClose() {
      this.dialogVisible = false;
      if (this.dialogSuccess && this.isVerified) {
        this.$router.push("/bank_details");
      }
    },
  },
};
</script>